/**
 * /fiscal/notas-fiscais/:id — Detalhe da nota fiscal (NF-e / NFS-e / NFC-e) + CC-e.
 *
 * Redesign FIN-9: casca .fin2 (EntityHeader .eh + .nfe-status + .block +
 * .dtable de CC-e + .fiscal-qr). Renderiza APENAS os campos que o serializer
 * de detalhe expõe (sem itens/protocolo/timeline fabricados do mockup).
 * Consome o backend fiscal existente — sem mudar lógica.
 */
import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  FiAlertTriangle, FiArrowLeft, FiCheckCircle, FiClock, FiDownload,
  FiEdit3, FiFileText, FiRefreshCw, FiX,
} from "react-icons/fi";

import AsyncButton from "../../../components/AsyncButton";
import SlideOverPanel from "../../../components/SlideOverPanel";
import { useAuth } from "../../../context/AuthContext";
import { formatarReais } from "../../../services/financeiro";
import {
  cancelarNotaFiscal, consultarSituacaoNota, emitirNotaFiscal,
  listarCartasCorrecao, obterNotaFiscal,
  SITUACAO_NF_LABEL, SITUACAO_NF_TONE, TIPO_DOC_FISCAL_LABEL,
} from "../../../services/fiscal";
import { PageContainer } from "../../../styles/shared";

import EmitirCceForm from "./EmitirCceForm";

const ABBR = { NFE: "NF", NFSE: "NS", NFCE: "NC" };

function fmtDataHora(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleString("pt-BR");
}
function fmtChave(chave) {
  return chave ? chave.replace(/(.{4})/g, "$1 ").trim() : "—";
}

function Campo({ label, children, mono, big }) {
  return (
    <div style={{ minWidth: 0, lineHeight: 1.3 }}>
      <div style={{ fontFamily: "var(--font-mono)", fontSize: 10, textTransform: "uppercase", letterSpacing: "0.08em", color: "var(--ink-3)", marginBottom: 4 }}>{label}</div>
      <div style={{ fontSize: big ? 24 : 14, fontWeight: big ? 700 : 500, color: "var(--ink)", fontFamily: mono ? "var(--font-mono)" : undefined, letterSpacing: big ? "-0.02em" : undefined, wordBreak: "break-word" }}>{children}</div>
    </div>
  );
}
function CampoGrid({ children, style }) {
  return <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(130px, 1fr))", gap: 16, ...style }}>{children}</div>;
}

export default function NotaFiscalDetalhe() {
  const navigate = useNavigate();
  const { id } = useParams();
  const { request, hasPermission } = useAuth();

  const [nota, setNota] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [ccesList, setCcesList] = useState([]);
  const [emitirCceAberto, setEmitirCceAberto] = useState(false);

  const carregar = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const data = await obterNotaFiscal(request, id);
      setNota(data);
      if ((data.tipo || "NFE") === "NFE") {
        try {
          const cces = await listarCartasCorrecao(request, id);
          setCcesList(Array.isArray(cces) ? cces : cces?.results || []);
        } catch { setCcesList([]); }
      }
    } catch (err) {
      setError(err?.message || "Erro ao carregar nota.");
    } finally { setLoading(false); }
  }, [request, id]);

  useEffect(() => { carregar(); }, [carregar]);

  if (loading && !nota) return <PageContainer className="fin2"><div className="empty"><div className="msg">Carregando…</div></div></PageContainer>;
  if (error && !nota) return <PageContainer className="fin2"><div className="empty"><div className="msg"><FiAlertTriangle size={14} /> {error}</div></div></PageContainer>;
  if (!nota) return null;

  const emitir = async () => setNota(await emitirNotaFiscal(request, id));
  const consultar = async () => setNota(await consultarSituacaoNota(request, id));
  const cancelar = async () => {
    const motivo = window.prompt("Justificativa de cancelamento (mínimo 15 caracteres):");
    if (motivo === null) return;
    if (motivo.trim().length < 15) { window.alert("Justificativa deve ter no mínimo 15 caracteres (regra SEFAZ)."); return; }
    setNota(await cancelarNotaFiscal(request, id, motivo.trim()));
  };

  const tipo = nota.tipo || "NFE";
  const ehNFe = tipo === "NFE";
  const tipoLabel = TIPO_DOC_FISCAL_LABEL[tipo] || "NF-e";
  // Estado da nota decide SE a ação existe; a permissão decide QUEM vê o
  // botão (hasPermission já faz bypass de Master).
  const temEmitir = hasPermission("fiscal:emitir");
  const podeEmitir = temEmitir && (nota.situacao === "PENDENTE" || nota.situacao === "ERRO");
  const podeConsultar = !!nota.chave_acesso;
  const podeEmitirCce = nota.pode_emitir_cce && ccesList.length < 20;
  const podeCancelar = nota.pode_cancelar && hasPermission("fiscal:cancelar");
  const proxSequencial = ccesList.length + 1;
  const danfeLabel = tipo === "NFSE" ? "NFS-e" : tipo === "NFCE" ? "DANFE NFC-e" : "DANFE";

  return (
    <PageContainer style={{ maxWidth: "none" }} className="fin2">
      <div className="breadcrumb" style={{ marginBottom: 14 }}>
        <span>Início</span><span className="crumb-sep">/</span>
        <span>Fiscal</span><span className="crumb-sep">/</span>
        <span className="link" role="button" tabIndex={0}
              onClick={() => navigate("/fiscal/notas-fiscais")}
              onKeyDown={(e) => e.key === "Enter" && navigate("/fiscal/notas-fiscais")}
              style={{ cursor: "pointer" }}>Notas Fiscais</span>
        <span className="crumb-sep">/</span>
        <span className="active">{nota.numero}</span>
      </div>

      <header className="eh eh--standalone">
        <button type="button" className="eh__back" onClick={() => navigate("/fiscal/notas-fiscais")}>
          <FiArrowLeft size={14} aria-hidden="true" /> Lista
        </button>
        <div className="eh__leading accent">{ABBR[tipo] || "NF"}</div>
        <div className="eh__id">
          <div className="eh__id-top">
            <h1 className="eh__title">{tipoLabel} {nota.numero}</h1>
            <span className={"badge no-dot " + (SITUACAO_NF_TONE[nota.situacao] || "neutral")}>
              {SITUACAO_NF_LABEL[nota.situacao] || nota.situacao}
            </span>
          </div>
          <div className="eh__sub">
            {nota.pedido_numero && (
              <>
                Pedido {nota.pedido_numero}
                {nota.pedido_situacao_display && ` (${nota.pedido_situacao_display})`}
                <span className="sep" />
              </>
            )}
            <span>{nota.cliente_nome || "—"}</span>
            {nota.emitida_em && <><span className="sep" />emitida {fmtDataHora(nota.emitida_em)}</>}
          </div>
        </div>
        <div className="eh__actions">
          {podeEmitir && (
            <AsyncButton variant="primary" onClick={emitir} successMessage="Nota emitida">
              <FiFileText size={14} aria-hidden="true" /> Emitir
            </AsyncButton>
          )}
          {podeConsultar && (
            <AsyncButton variant="secondary" onClick={consultar} successMessage="Situação atualizada">
              <FiRefreshCw size={14} aria-hidden="true" /> Consultar SEFAZ
            </AsyncButton>
          )}
          {nota.pdf_url && (
            <a className="btn btn--secondary" href={nota.pdf_url} target="_blank" rel="noreferrer">
              <FiDownload size={14} aria-hidden="true" /> {danfeLabel}
            </a>
          )}
          {nota.xml_url && (
            <a className="btn btn--secondary" href={nota.xml_url} target="_blank" rel="noreferrer">
              <FiDownload size={14} aria-hidden="true" /> XML
            </a>
          )}
          {podeCancelar && (
            <AsyncButton variant="danger" onClick={cancelar} silentError>
              <FiX size={14} aria-hidden="true" /> Cancelar
            </AsyncButton>
          )}
        </div>
      </header>

      {nota.erro_detalhe && (
        <div className="empty" style={{ marginTop: 12 }}>
          <div className="msg"><FiAlertTriangle size={14} aria-hidden="true" /> {nota.erro_detalhe}</div>
        </div>
      )}

      <div className="nfe-status" style={{ marginTop: 14 }}>
        <div className="field"><div className="lbl">Nº SEFAZ</div><div className="val mono">{nota.numero_nfe || "—"} · série {nota.serie}</div></div>
        <div className="field"><div className="lbl">{nota.emitida_em ? "Emitida em" : "Criada em"}</div><div className="val">{fmtDataHora(nota.emitida_em || nota.created_at)}</div></div>
        <div className="field"><div className="lbl">Gateway</div><div className="val">{nota.gateway_display}</div></div>
        <div className="field" style={{ gridColumn: "1 / -1" }}>
          <div className="lbl">Chave de acesso</div>
          <div className="val mono" style={{ fontSize: 12, wordBreak: "break-all", letterSpacing: "0.04em" }}>{fmtChave(nota.chave_acesso)}</div>
        </div>
      </div>

      <div className="card-grid-2" style={{ marginTop: 14 }}>
        <div className="block">
          <div className="block__head"><div className="block__title">Valores e tributação</div></div>
          <div className="block__body">
            <Campo label="Valor total" big>{formatarReais(nota.valor_total)}</Campo>
            <CampoGrid style={{ marginTop: 16 }}>
              <Campo label="ICMS">{formatarReais(nota.valor_icms)}</Campo>
              <Campo label="PIS / COFINS">{formatarReais(nota.valor_pis_cofins)}</Campo>
              <Campo label="ISS">{formatarReais(nota.valor_iss)}</Campo>
              {ehNFe && <Campo label="CFOP" mono>{nota.cfop || "—"}</Campo>}
              {ehNFe && <Campo label="Natureza">{nota.natureza_operacao || "—"}</Campo>}
            </CampoGrid>
          </div>
        </div>

        <div className="block">
          <div className="block__head"><div className="block__title">Dados da nota</div></div>
          <div className="block__body">
            <CampoGrid>
              <Campo label="Número interno" mono>{nota.numero}</Campo>
              <Campo label="Tipo">{tipoLabel}</Campo>
              <Campo label="Série" mono>{nota.serie}</Campo>
              <Campo label="Criada em">{fmtDataHora(nota.created_at)}</Campo>
              {nota.cancelada_em && <Campo label="Cancelada em">{fmtDataHora(nota.cancelada_em)}</Campo>}
              {tipo === "NFSE" && <Campo label="Cód. serviço" mono>{nota.codigo_servico || "—"}</Campo>}
              {tipo === "NFSE" && <Campo label="Alíquota ISS">{nota.aliquota_iss_percentual ? `${nota.aliquota_iss_percentual}%` : "—"}</Campo>}
              {tipo === "NFCE" && (
                <Campo label="CPF consumidor" mono>
                  {nota.cpf_consumidor
                    ? nota.cpf_consumidor.replace(/^(\d{3})(\d{3})(\d{3})(\d{2})$/, "$1.$2.$3-$4")
                    : "não identificado"}
                </Campo>
              )}
            </CampoGrid>
          </div>
        </div>
      </div>

      {tipo === "NFSE" && nota.descricao_servico && (
        <div className="block" style={{ marginTop: 14 }}>
          <div className="block__head"><div className="block__title">Descrição do serviço</div></div>
          <div className="block__body" style={{ whiteSpace: "pre-wrap", fontSize: 13, color: "var(--ink-2)" }}>{nota.descricao_servico}</div>
        </div>
      )}

      {tipo === "NFCE" && nota.qrcode_consulta && nota.situacao === "EMITIDA" && (
        <div className="block" style={{ marginTop: 14 }}>
          <div className="block__head"><div className="block__title">QR Code SEFAZ (consumidor)</div></div>
          <div className="block__body">
            <div className="fiscal-qr">
              {/* B20 (auditoria 2026-08-07): a imagem via api.qrserver.com
                  enviava a URL SEFAZ a um terceiro. O QR oficial já sai no
                  DANFE NFC-e impresso; aqui ficam a URL e o copiar. */}
              <div className="qr-meta">
                O consumidor escaneia o QR Code com a câmera do celular para consultar a NFC-e no portal SEFAZ.
                <div className="qr-k" style={{ marginTop: 8, wordBreak: "break-all" }}>{nota.qrcode_consulta}</div>
                <button type="button" className="btn btn--secondary" style={{ marginTop: 10 }}
                        onClick={() => { navigator.clipboard.writeText(nota.qrcode_consulta); window.alert("URL SEFAZ copiada."); }}>
                  Copiar URL
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {ehNFe && nota.situacao === "EMITIDA" && (
        <div className="block" style={{ marginTop: 14 }}>
          <div className="block__head">
            <div className="block__title">Cartas de Correção (CC-e) · {ccesList.length}/20</div>
            {temEmitir && (
              <button type="button" className="btn btn--secondary" disabled={!podeEmitirCce}
                      onClick={() => setEmitirCceAberto(true)}>
                <FiEdit3 size={14} aria-hidden="true" /> Nova CC-e
              </button>
            )}
          </div>
          <div className="block__body tight">
            {!nota.pode_emitir_cce && (
              <div style={{ margin: "12px 18px 0", padding: "10px 12px", background: "var(--warning-bg)", border: "1px solid var(--warning-border)", borderRadius: 8, fontSize: 12.5, color: "var(--ink-2)", display: "flex", gap: 8, alignItems: "center" }}>
                <FiClock size={14} aria-hidden="true" />
                <span>CC-e indisponível: fora da janela de 30 dias da emissão, limite de 20 atingido, ou a nota não está emitida.</span>
              </div>
            )}
            {ccesList.length === 0 ? (
              <div className="empty"><div className="msg">Nenhuma carta de correção emitida.</div></div>
            ) : (
              <table className="dtable">
                <colgroup><col style={{ width: 56 }} /><col /><col style={{ width: 160 }} /><col style={{ width: 90 }} /><col style={{ width: 110 }} /></colgroup>
                <thead><tr><th>Seq.</th><th>Texto</th><th>Emitida em</th><th>Status</th><th>Documentos</th></tr></thead>
                <tbody>
                  {ccesList.map((c) => (
                    <tr key={c.id}>
                      <td className="mono">#{c.sequencial}</td>
                      <td className="ell" title={c.texto}>{c.texto}</td>
                      <td className="mono">{fmtDataHora(c.created_at)}</td>
                      <td>
                        {c.erro_detalhe
                          ? <span className="badge no-dot danger" title={c.erro_detalhe}>Erro</span>
                          : <span className="badge no-dot success"><FiCheckCircle size={11} aria-hidden="true" /> OK</span>}
                      </td>
                      <td>
                        <div style={{ display: "flex", gap: 10 }}>
                          {c.pdf_url && <a href={c.pdf_url} target="_blank" rel="noreferrer" style={{ fontSize: 11 }}>PDF</a>}
                          {c.xml_url && <a href={c.xml_url} target="_blank" rel="noreferrer" style={{ fontSize: 11 }}>XML</a>}
                          {!c.pdf_url && !c.xml_url && <span className="tiny muted">—</span>}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}

      {nota.observacao_fiscal && (
        <div className="block" style={{ marginTop: 14 }}>
          <div className="block__head"><div className="block__title">Observação fiscal</div></div>
          <div className="block__body" style={{ whiteSpace: "pre-wrap", fontSize: 13, color: "var(--ink-2)" }}>{nota.observacao_fiscal}</div>
        </div>
      )}

      {nota.motivo_cancelamento && (
        <div className="block" style={{ marginTop: 14 }}>
          <div className="block__head"><div className="block__title">Motivo do cancelamento</div></div>
          <div className="block__body" style={{ fontSize: 13, color: "var(--ink-2)" }}>{nota.motivo_cancelamento}</div>
        </div>
      )}

      <SlideOverPanel open={emitirCceAberto} onClose={() => setEmitirCceAberto(false)}
                      title="Emitir Carta de Correção" subtitle={`Sequencial sugerido: ${proxSequencial}`} width={560}>
        {emitirCceAberto && (
          <EmitirCceForm notaId={id} sequencialSugerido={proxSequencial}
                         onCancel={() => setEmitirCceAberto(false)}
                         onEmitted={() => { setEmitirCceAberto(false); carregar(); }} />
        )}
      </SlideOverPanel>
    </PageContainer>
  );
}
